import { useCallback, useEffect, useMemo, useState, type ReactNode, type Ref } from "react";
import { useTranslation } from "react-i18next";
import {
  Archive,
  BookmarkPlus,
  Check,
  ChevronDown,
  ClipboardList,
  EyeOff,
  Folder,
  Loader2,
  ListTree,
  MoreHorizontal,
  Plus,
} from "lucide-react";

import { ModelDropdown } from "@/components/model-dropdown";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAccessibleProjects } from "@/hooks/use-accessible-projects";
import { useTasks } from "@/hooks/use-tasks";
import {
  addSessionToList,
  removeSessionFromList,
  supportsSessionParticipation,
} from "@/api/session-participation";
import { notifyProjectsChanged, notifyTaskChanged } from "@/lib/project-events";
import { DEFAULT_SESSION_TITLE, isProjectTaskSession, type Session } from "@/lib/session";
import type { AccessibleProjectSummary, Task, TaskStatus } from "@/lib/project-types";
import { cn, type AccessTokenGetter } from "@/lib/utils";

export interface ConversationActionsMenuProps {
  agentId: string | null;
  sessionId: string;
  getToken: AccessTokenGetter;
  showModelPicker?: boolean;
  onArchive?: (id: string) => void | Promise<unknown>;
  /** Opens the project picker to turn this conversation into a task. */
  onConvertToTask?: () => void;
  onAddSubtask?: () => void;
  /** Opens the timeline from the mobile header overflow menu. */
  onViewTimeline?: () => void;
  conversationActionsLabel: string;
  archiveConversationLabel: string;
  /** Overrides the archive label for a conversation backed by a project task. */
  archiveTaskLabel?: string;
  convertToTaskLabel: string;
  canManageSession: boolean;
  /** Active session data used for participation capability and state. */
  session?: Session;
  /** Current caller ID, used to suppress participation controls for owners. */
  currentUserId?: string | null;
  /** Parent-owned mutations refresh the authoritative sidebar session list. */
  onAddParticipation?: (id: string) => Promise<void>;
  onRemoveParticipation?: (id: string) => Promise<void>;
  /** Used only by the direct-API compatibility fallback. */
  onParticipationChanged?: () => void | Promise<void>;
  /** The presence of onClose identifies an embedded chat panel. */
  onClose?: () => void;
  /** Compact header controls moved here when the rendered header is narrow. */
  overflowContent?: ReactNode;
  /** Allows dialogs opened from this menu to restore focus to its trigger. */
  triggerRef?: Ref<HTMLButtonElement>;
}

/** Project membership and inherited sharing do not make a session a task. */
export function canConvertConversationToTask({
  isReadOnlySubAgent,
  isTaskConversation,
  taskStatus,
  session,
}: {
  isReadOnlySubAgent: boolean;
  isTaskConversation: boolean;
  taskStatus: TaskStatus | undefined;
  session: Session | null | undefined;
}): boolean {
  return !isReadOnlySubAgent
    && taskStatus === undefined
    && !isTaskConversation
    && !isProjectTaskSession(session ?? { metadata: {} });
}

/**
 * Conversation actions, plus any header controls that need overflow. Embedded
 * panels keep their action menu hidden but can still expose moved controls.
 */
export function ConversationActionsMenu({
  agentId,
  sessionId,
  getToken,
  showModelPicker = false,
  onArchive,
  onConvertToTask,
  onAddSubtask,
  onViewTimeline,
  conversationActionsLabel,
  archiveConversationLabel,
  archiveTaskLabel,
  convertToTaskLabel,
  canManageSession,
  session,
  currentUserId,
  onAddParticipation,
  onRemoveParticipation,
  onParticipationChanged,
  onClose,
  overflowContent,
  triggerRef,
}: ConversationActionsMenuProps) {
  const { t } = useTranslation("chat");
  const hasActions = !onClose && canManageSession;
  const hasOverflowContent = Boolean(overflowContent);
  const isOwner = Boolean(session && currentUserId && session.user_id === currentUserId);
  const [participationSupported, setParticipationSupported] = useState(false);
  const [participationMutating, setParticipationMutating] = useState(false);
  const [participationFailed, setParticipationFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setParticipationSupported(false);
    if (isOwner || session?.can_participate === false) return;

    // Newer servers include the per-session capability. Older ones can still
    // opt in through the narrowly scoped, failure-safe endpoint probe.
    if (session?.can_participate === true) {
      setParticipationSupported(true);
      return;
    }
    void supportsSessionParticipation(sessionId, getToken).then((supported) => {
      if (active) setParticipationSupported(supported);
    });
    return () => { active = false; };
  }, [getToken, isOwner, session, sessionId]);

  const canChangeParticipation = !isOwner && participationSupported;
  const removeFromList = session?.is_pinned === true;
  const changeParticipation = async () => {
    if (participationMutating) return;
    setParticipationMutating(true);
    setParticipationFailed(false);
    try {
      if (removeFromList) {
        if (onRemoveParticipation) {
          await onRemoveParticipation(sessionId);
        } else {
          await removeSessionFromList(sessionId, getToken);
          await onParticipationChanged?.();
        }
      } else if (onAddParticipation) {
        await onAddParticipation(sessionId);
      } else {
        await addSessionToList(sessionId, getToken);
        await onParticipationChanged?.();
      }
    } catch {
      setParticipationFailed(true);
      if (!(removeFromList ? onRemoveParticipation : onAddParticipation)) {
        await onParticipationChanged?.();
      }
    } finally {
      setParticipationMutating(false);
    }
  };
  if (!hasActions && !hasOverflowContent && !canChangeParticipation && !onViewTimeline) return null;

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          ref={triggerRef}
          type="button"
          variant="ghost"
          size="icon"
          className="size-7 shrink-0"
          aria-label={conversationActionsLabel}
          title={conversationActionsLabel}
        >
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {hasOverflowContent && (
          <div data-testid="conversation-actions-overflow-content">
            {overflowContent}
          </div>
        )}
        {onViewTimeline && (
          <DropdownMenuItem onSelect={onViewTimeline}>
            <ListTree className="size-4" aria-hidden="true" />
            {t("navigation.viewTimeline")}
          </DropdownMenuItem>
        )}
        {hasOverflowContent && (hasActions || canChangeParticipation || onViewTimeline) && <DropdownMenuSeparator />}
        {hasActions && showModelPicker && (
          <>
            <ModelDropdown
              agentId={agentId}
              sessionId={sessionId}
              getToken={getToken}
              embedded
              compact
            />
            {(onConvertToTask || canChangeParticipation || hasActions) && <DropdownMenuSeparator />}
          </>
        )}
        {hasActions && onConvertToTask && (
          <>
            <DropdownMenuItem
              className="text-xs"
              onSelect={() => onConvertToTask()}
            >
              <ClipboardList className="size-4" />
              {convertToTaskLabel}
            </DropdownMenuItem>
            {(canChangeParticipation || hasActions) && <DropdownMenuSeparator />}
          </>
        )}
        {onAddSubtask && <DropdownMenuItem className="text-xs" onSelect={onAddSubtask}><Plus className="size-4" />{t("projects:subtasks.add")}</DropdownMenuItem>}
        {canChangeParticipation && (
          <>
            <DropdownMenuItem
              className="text-xs"
              disabled={participationMutating}
              onSelect={() => { void changeParticipation(); }}
            >
              {removeFromList
                ? <EyeOff className="size-4" />
                : <BookmarkPlus className="size-4" />}
              {t(removeFromList ? "participation.removeFromList" : "participation.addToList")}
            </DropdownMenuItem>
            {hasActions && <DropdownMenuSeparator />}
          </>
        )}
        {hasActions && (
          <DropdownMenuItem
            className="text-xs text-destructive focus:text-destructive"
            onSelect={() => {
              void onArchive?.(sessionId);
            }}
          >
            <Archive className="size-4" />
            {archiveTaskLabel ?? archiveConversationLabel}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
    {participationFailed && (
      <ParticipationFailureAnnouncement removeFromList={removeFromList} />
    )}
    </>
  );
}

function ParticipationFailureAnnouncement({ removeFromList }: { removeFromList: boolean }) {
    const { t } = useTranslation("chat");
    return (
      <span role="alert" className="sr-only">
        {t(removeFromList ? "participation.removeFailed" : "participation.addFailed")}
      </span>
    );
}

interface ConvertToTaskDialogProps {
  session: Pick<Session, "id" | "title"> & Partial<Pick<Session, "project_id">>;
  /** Use thread dialog copy even when the thread has no source quote. */
  isThread?: boolean;
  /** The immutable source quote when converting a manual thread. */
  sourceQuote?: string;
  getToken: AccessTokenGetter;
  onTaskCreated?: (sessionId: string, task: Task) => void | Promise<unknown>;
  onUnboundTaskCreated?: () => void;
  onClose: () => void;
}

/**
 * Selects from the all-accessible-projects catalog rather than only the
 * sidebar's pinned projects. The backend remains the authority for access at
 * creation time, which covers role or archive changes after this list loads.
 */
export function ConvertToTaskDialog({
  session,
  isThread = false,
  sourceQuote,
  getToken,
  onTaskCreated,
  onUnboundTaskCreated,
  onClose,
}: ConvertToTaskDialogProps) {
  const { t } = useTranslation();
  const [projectOpen, setProjectOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [chosenProject, setChosenProject] = useState<AccessibleProjectSummary | null>(null);
  const [creating, setCreating] = useState(false);
  const [createdWithoutSession, setCreatedWithoutSession] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const {
    projects,
    hasMore,
    isLoading: projectsLoading,
    error: projectsError,
    setSearchQuery,
    loadMore,
  } = useAccessibleProjects(getToken);
  const { createWithSession } = useTasks(getToken);
  // Only lock a session's project when the accessible catalog resolves it.
  // A later page or a removed project may leave the ID unresolved; then the
  // normal picker lets the user choose a visible project before creation.
  const fixedProject = useMemo(
    () => projects.find((project) => project.id === session.project_id) ?? null,
    [projects, session.project_id],
  );
  // Keep an explicit picker choice across search and page resets. It came
  // from the catalog even if its page is no longer loaded when the picker closes.
  const selectedProject = fixedProject ?? chosenProject;
  const projectId = selectedProject?.id ?? "";
  const title = session.title.trim() || DEFAULT_SESSION_TITLE;

  useEffect(() => {
    if (projectOpen) return;
    setQuery("");
    setSearchQuery("");
  }, [projectOpen, setSearchQuery]);

  const handleProjectQueryChange = useCallback((value: string) => {
    setQuery(value);
    setSearchQuery(value);
  }, [setSearchQuery]);

  const handleConfirm = useCallback(async () => {
    if (!selectedProject || creating || createdWithoutSession) return;

    setActionError(null);
    setCreating(true);
    try {
      const result = await createWithSession(
        projectId,
        session.id,
        title,
        sourceQuote,
      );
      if (!result.task) {
        setActionError(result.status === 409
          ? session.project_id && projectId !== session.project_id
            ? t("projects:taskSessionOtherProject")
            : session.project_id
              ? t("projects:taskSessionProjectConflict")
              : t("projects:taskSessionAlreadyBound")
          : result.status === 422
            ? t("projects:taskSessionUnsupported")
            : t("projects:taskCreateFailed"));
        return;
      }
      const task = result.task;

      if (task.session_id !== session.id) {
        // Older servers can ignore an unknown session_id while creating an
        // unbound task. Report this explicitly and prevent a duplicate retry.
        setActionError(t("projects:taskCreatedWithoutSession"));
        setCreatedWithoutSession(true);
        notifyTaskChanged(task);
        notifyProjectsChanged();
        onUnboundTaskCreated?.();
        return;
      }

      // Independent hooks power the project sidebar and project workspace.
      // Broadcast the confirmed task, then invalidate the project list so its
      // folder and aggregate assignment state stay current as well.
      notifyTaskChanged(task);
      notifyProjectsChanged();
      try {
        await onTaskCreated?.(session.id, task);
      } catch {
        // The task was created successfully even if a local view refresh
        // fails; closing avoids inviting a duplicate submission.
      }
      onClose();
    } finally {
      setCreating(false);
    }
  }, [createWithSession, creating, createdWithoutSession, onClose, onTaskCreated, onUnboundTaskCreated, projectId, selectedProject, session.id, session.project_id, sourceQuote, t, title]);

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !creating) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(isThread ? "threads.convertToTaskDialogTitle" : "sidebar.convertToTaskDialogTitle")}</DialogTitle>
          <DialogDescription>
            {t(isThread
              ? fixedProject ? "threads.convertToTaskDialogDescFixed" : "threads.convertToTaskDialogDesc"
              : fixedProject ? "sidebar.convertToTaskDialogDescFixed" : "sidebar.convertToTaskDialogDesc",
            { title, project: fixedProject?.name })}
          </DialogDescription>
        </DialogHeader>

        <Popover
          open={!fixedProject && projectOpen}
          onOpenChange={(open) => {
            if (!creating && !fixedProject) setProjectOpen(open);
          }}
        >
          <span
            tabIndex={fixedProject ? 0 : undefined}
            role={fixedProject ? "group" : undefined}
            aria-label={fixedProject ? fixedProject.name : undefined}
            className="block"
          >
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                aria-expanded={!fixedProject && projectOpen}
                aria-label={t("sidebar.selectProject")}
                aria-disabled={Boolean(fixedProject) || creating}
                disabled={Boolean(fixedProject) || creating}
                className="w-full justify-between font-normal"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <Folder className="size-4 shrink-0 text-amber-500" />
                  <span className={cn("truncate", !selectedProject && "text-muted-foreground")}>
                    {selectedProject?.name ?? t("sidebar.selectProject")}
                  </span>
                </span>
                {fixedProject
                  ? <Check className="size-4 shrink-0 text-primary" />
                  : <ChevronDown className="size-4 shrink-0 text-muted-foreground" />}
              </Button>
            </PopoverTrigger>
          </span>
          <PopoverContent
            align="start"
            className="z-[60] w-[min(24rem,calc(100vw-2rem))] p-0"
            onEscapeKeyDown={(event) => {
              event.preventDefault();
              setProjectOpen(false);
            }}
          >
            <Command shouldFilter={false}>
              <CommandInput
                autoFocus
                value={query}
                onValueChange={handleProjectQueryChange}
                placeholder={t("sidebar.selectProject")}
              />
              <CommandList>
                {projectsError && (
                  <div className="px-3 py-2 text-sm text-destructive">{projectsError}</div>
                )}
                {projectsLoading && projects.length === 0 && (
                  <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                    <Loader2 className="size-4 animate-spin" />
                    {t("projects:loadingProjects")}
                  </div>
                )}
                {!projectsLoading && !projectsError && projects.length === 0 && (
                  <CommandEmpty>{t("projects:catalog.empty")}</CommandEmpty>
                )}
                <CommandGroup heading={t("projects:sectionProjects")}>
                  {projects.map((project) => (
                    <CommandItem
                      key={project.id}
                      value={`${project.name} ${project.description_preview}`}
                      disabled={creating}
                      onSelect={() => {
                        setChosenProject(project);
                        setActionError(null);
                        setProjectOpen(false);
                      }}
                    >
                      <Folder className="size-4 shrink-0 text-amber-500" />
                      <span className="min-w-0 flex-1 truncate">{project.name}</span>
                      {project.id === projectId && <Check className="size-4 text-primary" />}
                    </CommandItem>
                  ))}
                  {hasMore && (
                    <div className="px-2 py-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={projectsLoading}
                        className="w-full"
                        onClick={() => void loadMore()}
                      >
                        {t("projects:catalog.loadMore")}
                      </Button>
                    </div>
                  )}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {actionError && <p className="text-sm text-destructive">{actionError}</p>}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={creating}>
            {t("common:actions.cancel")}
          </Button>
          <Button type="button" onClick={() => void handleConfirm()} disabled={!selectedProject || creating || createdWithoutSession}>
            {creating && <Loader2 className="animate-spin" />}
            {t("projects:addTask")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
